import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { Mail, Phone, MapPin, ArrowRight, Clock, CheckCircle2 } from 'lucide-react';
import { GET_SITE_SETTINGS, SUBMIT_CONTACT_ENQUIRY } from '../graphql/coresite';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }

    .grain {
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
    }

    .gold-rule { height: 1px; background: linear-gradient(90deg, ${GOLD} 0%, rgba(184,151,90,0.1) 100%); }

    .u-field {
      width: 100%;
      font-family: 'Jost', sans-serif; font-size:16px; font-weight: 400;
      color: #0f172a; background: transparent;
      border: none; border-bottom: 1px solid #ddd9d2;
      padding: 12px 0; outline: none;
      transition: border-color 0.3s;
    }
    .u-field:focus { border-bottom-color: ${GOLD}; }
    .u-field::placeholder { color: #b8b2aa; font-weight: 300; }
    .u-field-label {
      display: block; font-family: 'Jost', sans-serif;
      font-size:13px; font-weight: 600;
      letter-spacing:0.2em; text-transform: uppercase;
      color:#12100e; margin-bottom: 8px;
    }

    .u-select {
      width: 100%; appearance: none; cursor: pointer;
      font-family: 'Jost', sans-serif; font-size:16px; font-weight: 400;
      color: #0f172a; background: transparent;
      border: none; border-bottom: 1px solid #ddd9d2;
      padding: 12px 0; outline: none;
      transition: border-color 0.3s;
    }
    .u-select:focus { border-bottom-color: ${GOLD}; }

    .contact-item {
      display: flex; align-items: flex-start; gap: 16px;
      padding: 20px 0; border-bottom: 1px solid #f0ece6;
      transition: opacity 0.2s;
    }
    .contact-item:last-child { border-bottom: none; }
    .contact-icon {
      width: 36px; height: 36px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid ${GOLD_DIM};
      transition: all 0.3s;
    }
    .contact-item:hover .contact-icon {
      border-color: ${GOLD};
      background: ${GOLD_DIM};
    }

    .primary-cta {
      position: relative;
      display: inline-flex; align-items: center; gap: 10px;
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase;
      padding: 15px 32px; border: 1px solid ${NAVY};
      background: ${NAVY}; color: #fff;
      overflow: hidden; transition: all 0.35s cubic-bezier(0.22,1,0.36,1);
      cursor: pointer;
    }
    .primary-cta::before {
      content: ''; position: absolute; inset: 0;
      background: ${GOLD}; transform: scaleX(0); transform-origin: left;
      transition: transform 0.35s cubic-bezier(0.22,1,0.36,1); z-index: 0;
    }
    .primary-cta:hover::before { transform: scaleX(1); }
    .primary-cta:hover { color: ${NAVY}; border-color: ${GOLD}; }
    .primary-cta span, .primary-cta svg { position: relative; z-index: 1; }
    .primary-cta:disabled { opacity: 0.5; cursor: not-allowed; }
    .primary-cta:disabled::before { display: none; }

    .office-card {
      border: 1px solid #e8e4de; background: #fff;
      transition: border-color 0.3s;
    }
    .office-card:hover { border-color: ${GOLD}; }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .fade-up { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) both; }

    @keyframes popIn {
      0%  { transform: scale(0.6); opacity: 0; }
      70% { transform: scale(1.08); }
      100%{ transform: scale(1);   opacity: 1; }
    }
    .pop-in { animation: popIn 0.5s cubic-bezier(0.22,1,0.36,1) both; }

    @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }
    .pulse-dot { animation: pulse 2s ease-in-out infinite; }

    .error-bar {
      background: #fef2f2; border: 1px solid #fecaca;
      color: #b91c1c; padding: 10px 14px;
      font-family: 'Jost', sans-serif; font-size:15px;
      margin-bottom: 16px;
    }
  `}</style>
);

/* ─── Helpers ─── */
const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-6 h-px" style={{ background: GOLD }} />
    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>{text}</span>
  </div>
);

/* ─── Contact detail row ─── */
const ContactItem = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="contact-item">
      <div className="contact-icon">
        <Icon size={14} strokeWidth={1.5} style={{ color: GOLD }} />
      </div>
      <div>
        <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>
          {label}
        </p>
        <p className="f-jost text-[15px] text-slate-700 font-light leading-relaxed" style={{ whiteSpace: 'pre-line' }}>
          {value}
        </p>
      </div>
    </div>
  );
};

/* ─── Subject choices — must stay in sync with ContactEnquiry.SUBJECT_CHOICES ─── */
const SUBJECT_OPTIONS = [
  'Tailor-made safari planning',
  'Wildlife & Safari',
  'Alpine Adventure',
  'Cultural Immersion',
  'Gastronomy & Wine',
  'Corporate Retreat',
  'Private Consultation',
];

const EMPTY_FORM = {
  name: '', email: '', phone: '',
  subject: SUBJECT_OPTIONS[0],
  travelers: '2',
  message: '',
};

/* ═══════════════════════════════════════════
   CONTACT PAGE
═══════════════════════════════════════════ */
const ContactPage = () => {
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [submitted,   setSubmitted]   = useState(false);
  const [serverError, setServerError] = useState('');

  /* ── Data ── */
  const { data: settingsData } = useQuery(GET_SITE_SETTINGS, { fetchPolicy: 'cache-first' });
  const settings = settingsData?.siteSettings || {};

  /* ── Mutation ── */
  const [submitEnquiry, { loading: isSubmitting }] = useMutation(SUBMIT_CONTACT_ENQUIRY);

  /* ── Handlers ── */
  const handleChange = e => {
    setServerError('');
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    try {
      const { data } = await submitEnquiry({
        variables: {
          name:      formData.name,
          email:     formData.email,
          phone:     formData.phone     || undefined,
          subject:   formData.subject   || undefined,
          travelers: formData.travelers || undefined,
          message:   formData.message,
        },
      });

      const result = data?.submitContactEnquiry;

      if (result?.success) {
        setSubmitted(true);
        setFormData(EMPTY_FORM);
      } else {
        setServerError(
          result?.message === 'validation_error'
            ? 'Please fill in all required fields.'
            : 'Something went wrong — please try again or email us directly.',
        );
      }
    } catch {
      setServerError('A network error occurred. Please check your connection and try again.');
    }
  };

  /* ── Derived contact details from SiteSettings / Agency ── */
  const locationLine = [settings.address, settings.city]
    .filter(Boolean)
    .join('\n') || null;

  const officeHoursLine = 'Mon – Fri, 9 AM – 6 PM';   // not yet on model; static for now

  return (
    <div className="f-jost min-h-screen" style={{ background: '#F5F3EF' }}>
      <PageStyles />

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative overflow-hidden dark-sec" style={{ background: NAVY, paddingTop: '7rem', paddingBottom: '5rem' }}>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(105deg, rgba(8,14,26,0.95) 40%, rgba(8,14,26,0.55))' }} />
        <div className="grain absolute inset-0 pointer-events-none opacity-60" />

        <div className="absolute left-0 top-0 bottom-0 w-px hidden lg:block"
          style={{ marginLeft: '10%', background: `linear-gradient(to bottom, transparent, ${GOLD} 30%, ${GOLD} 70%, transparent)`, opacity: 0.2 }} />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-8 h-px" style={{ background: GOLD }} />
              <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                Concierge Desk
              </span>
            </div>

            <h1 className="f-cormorant text-white font-light leading-[0.92] mb-5"
              style={{ fontSize: 'clamp(52px,6.5vw,80px)' }}>
              Begin the<br />
              <span style={{ color: GOLD }}>Conversation.</span>
            </h1>

            <p className="f-jost text-slate-400 text-[16px] font-light leading-relaxed max-w-md">
              Our travel architects are ready to design your next extraordinary chapter.
              Every great safari starts with a conversation.
            </p>

            <div className="flex items-center gap-3 mt-8 pt-8 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: '#4ade80' }} />
              <span className="f-jost text-[13px] uppercase tracking-[0.16em]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Specialists online · Typical reply within 2 hours
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-4 space-y-6">

            {/* Direct contacts — from SiteSettings → Agency */}
            <div style={{ background: '#fff', border: '1px solid #e8e4de' }} className="p-7">
              <SectionLabel text="Reach Us Directly" />
              <h3 className="f-cormorant text-slate-800 font-light mb-6" style={{ fontSize: '29px' }}>
                We'd love to hear from you.
              </h3>

              <div>
                <ContactItem icon={MapPin} label="Global HQ"    value={locationLine}    />
                <ContactItem icon={Phone}  label="Phone"        value={settings.phone}  />
                <ContactItem icon={Mail}   label="Email"        value={settings.primaryEmail} />
                <ContactItem icon={Clock}  label="Office Hours" value={officeHoursLine} />
              </div>
            </div>

            {/* Social quick-links — only rendered when at least one URL is present */}
            {(settings.instagramUrl || settings.facebookUrl || settings.linkedinUrl || settings.twitterUrl) && (
              <div style={{ background: '#fff', border: '1px solid #e8e4de' }} className="p-7">
                <SectionLabel text="Follow Us" />
                <div className="flex flex-wrap gap-3 mt-2">
                  {[
                    { label: 'Instagram', href: settings.instagramUrl },
                    { label: 'Facebook',  href: settings.facebookUrl  },
                    { label: 'LinkedIn',  href: settings.linkedinUrl  },
                    { label: 'X / Twitter', href: settings.twitterUrl },
                  ]
                    .filter(s => s.href)
                    .map(({ label, href }) => (
                      <a
                        key={label}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] px-3 py-2 border transition-colors"
                        style={{ borderColor: '#e8e4de', color: '#12100e' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = NAVY; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e4de'; e.currentTarget.style.color = '#64748b'; }}
                      >
                        {label}
                      </a>
                    ))
                  }
                </div>
              </div>
            )}

          </div>

          {/* ── RIGHT COLUMN: FORM ── */}
          <div className="lg:col-span-8" style={{ background: '#fff', border: '1px solid #e8e4de' }}>
            {submitted ? (
              /* ── Success state ── */
              <div className="flex flex-col items-center justify-center text-center py-24 px-8 fade-up">
                <div
                  className="pop-in w-16 h-16 flex items-center justify-center mb-8"
                  style={{ border: `1px solid ${GOLD}`, background: GOLD_DIM }}
                >
                  <CheckCircle2 size={28} strokeWidth={1.5} style={{ color: GOLD }} />
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-px" style={{ background: GOLD }} />
                  <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                    Enquiry Received
                  </span>
                  <div className="w-8 h-px" style={{ background: GOLD }} />
                </div>

                <h2 className="f-cormorant text-slate-900 font-light mb-3" style={{ fontSize: '36px', lineHeight: 1 }}>
                  Your enquiry is with us.
                </h2>
                <p className="f-jost text-[15px] text-slate-500 font-light max-w-sm leading-relaxed mb-8">
                  One of our travel architects will be in touch within 2 hours.
                  We look forward to planning your safari together.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="primary-cta"
                >
                  <span>Send Another Enquiry</span>
                  <ArrowRight size={12} />
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <form onSubmit={handleSubmit}>
                <div className="p-8 sm:p-12 space-y-10">

                  {/* Personal details */}
                  <div>
                    <SectionLabel text="Your Details" />
                    <h2 className="f-cormorant text-slate-900 font-light mb-7" style={{ fontSize: '30px' }}>
                      Tell us about yourself
                    </h2>

                    {serverError && (
                      <div className="error-bar mb-6">{serverError}</div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                      <div>
                        <label className="u-field-label">Full Name</label>
                        <input
                          type="text" name="name" value={formData.name} onChange={handleChange}
                          className="u-field" placeholder="Eleanor Ashworth" required
                        />
                      </div>
                      <div>
                        <label className="u-field-label">Email Address</label>
                        <input
                          type="email" name="email" value={formData.email} onChange={handleChange}
                          className="u-field" placeholder="eleanor@email.com" required
                        />
                      </div>
                      <div>
                        <label className="u-field-label">Phone (optional)</label>
                        <input
                          type="tel" name="phone" value={formData.phone} onChange={handleChange}
                          className="u-field" placeholder="+44 7700 900000"
                        />
                      </div>
                      <div>
                        <label className="u-field-label">Number of Travellers</label>
                        <select name="travelers" value={formData.travelers} onChange={handleChange} className="u-select">
                          {['1', '2', '3–4', '5–8', '9+'].map(o => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="gold-rule" />

                  {/* Journey details */}
                  <div>
                    <SectionLabel text="Your Safari" />
                    <h2 className="f-cormorant text-slate-900 font-light mb-7" style={{ fontSize: '30px' }}>
                      What are you envisioning?
                    </h2>

                    <div className="mb-8">
                      <label className="u-field-label">Type of Journey</label>
                      <div className="relative">
                        <select
                          name="subject" value={formData.subject} onChange={handleChange}
                          className="u-select"
                        >
                          {SUBJECT_OPTIONS.map(o => <option key={o}>{o}</option>)}
                        </select>
                        <div className="absolute right-0 bottom-3 pointer-events-none">
                          <div className="w-3 h-px mb-0.5" style={{ background: '#94a3b8' }} />
                          <div className="w-3 h-px" style={{ background: '#94a3b8' }} />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="u-field-label">Your Vision</label>
                      <textarea
                        name="message" value={formData.message} onChange={handleChange}
                        rows={5} required
                        placeholder="Tell us about the safari you have in mind — parks, dates, who's travelling, the kind of camps you like…"
                        className="u-field resize-none"
                        style={{ borderBottom: '1px solid #ddd9d2', paddingTop: '12px' }}
                      />
                    </div>
                  </div>

                </div>

                {/* CTA area */}
                <div className="px-8 sm:px-12 pb-8 sm:pb-12 border-t" style={{ borderColor: '#f0ece6', paddingTop: '24px' }}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="primary-cta"
                    >
                      <span>{isSubmitting ? 'Sending…' : 'Send Enquiry'}</span>
                      {!isSubmitting && <ArrowRight size={12} />}
                    </button>

                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full pulse-dot" style={{ background: '#4ade80' }} />
                      <span className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400">
                        Specialists online now
                      </span>
                    </div>
                  </div>

                  <p className="f-jost text-[13px] text-slate-400 font-light mt-4">
                    By submitting, you agree to our{' '}
                    <a href="/privacy" className="underline underline-offset-2 hover:text-slate-700 transition-colors">Privacy Policy</a>.
                    We never share your details with third parties.
                  </p>
                </div>
              </form>
            )}
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════
          PROCESS STRIP
      ══════════════════════════════════════ */}
      <section className="border-t" style={{ borderColor: '#e2ddd6', background: '#fff' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-14">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-6 h-px" style={{ background: GOLD }} />
            <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
              What Happens Next
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px" style={{ border: '1px solid #e8e4de' }}>
            {[
              { num: '01', title: 'We review your enquiry', desc: 'A specialist reads every submission personally and matches you with the safari specialist who knows those parks best.' },
              { num: '02', title: 'Consultation call',      desc: 'We arrange a 30-minute call to understand your travel style, preferences, and aspirations.'         },
              { num: '03', title: 'Bespoke proposal',       desc: 'Within 48 hours you receive a fully crafted itinerary designed around your vision.'                 },
            ].map(({ num, title, desc }) => (
              <div key={num} className="p-8" style={{ borderRight: '1px solid #e8e4de' }}>
                <p className="f-cormorant font-light mb-4" style={{ fontSize: '42px', lineHeight: 1, color: GOLD }}>{num}</p>
                <div className="gold-rule mb-4" style={{ width: '32px', background: GOLD }} />
                <h4 className="f-cormorant text-slate-900 font-light text-[25px] mb-2">{title}</h4>
                <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactPage;